/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, CheckCircle, XCircle, Clock, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172800) return 'yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * Background-async smoke test runner button + live status strip.
 * Props:
 *   - canRun (bool): if false, button hidden (viewer mode)
 *   - compact (bool): if true, one-line layout for dashboard widget
 *   - onChange(run): callback fired whenever status updates
 */
export default function TestRunnerLauncher({ canRun = true, compact = false, onChange }) {
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null); // current run being tracked
  const pollTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollRun = useCallback(async (runId) => {
    try {
      const { api } = await import('../lib/api');
      const r = await api.get(`/admin/test-runs/${runId}`);
      setCurrent(r.data);
      if (onChange) onChange(r.data);
      if (r.data.status !== 'running') {
        stopPolling();
        setRunning(false);
        if (r.data.success) {
          toast.success(`✓ ${r.data.passed} passed in ${Number(r.data.duration_sec || 0).toFixed(1)}s`);
        } else {
          toast.error(`✗ ${r.data.failed || 0} failed · ${r.data.errors || 0} errors`);
        }
      }
    } catch (err) {
      console.error('Poll failed:', err?.message);
    }
  }, [onChange, stopPolling]);

  // Check for in-flight run on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import('../lib/api');
        const r = await api.get('/admin/test-runs?limit=1');
        const last = r.data?.runs?.[0];
        if (!cancelled && last) {
          setCurrent(last);
          if (onChange) onChange(last);
          if (last.status === 'running') {
            setRunning(true);
            pollTimerRef.current = setInterval(() => pollRun(last.id), 2000);
          }
        }
      } catch { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, []);

  const handleRun = async () => {
    if (!canRun || running) return;
    if (!window.confirm("Smoke suite run karna hai?\nBackground mein chalega — UI freeze nahi hogi.")) return;
    setRunning(true);
    try {
      const { api } = await import('../lib/api');
      const r = await api.post('/admin/run-tests');
      const runId = r.data?.id;
      if (r.data.already_running) {
        toast.info('Already running — polling existing run');
      } else {
        toast.message('Tests started — running in background…');
      }
      setCurrent(r.data);
      if (onChange) onChange(r.data);
      // Start polling
      stopPolling();
      pollTimerRef.current = setInterval(() => pollRun(runId), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start');
      setRunning(false);
    }
  };

  const isRunning = running || current?.status === 'running';
  const isFailure = current && current.status !== 'running' && current.success === false;
  const completedAt = current?.finished_at || current?.started_at;

  // Auto-tick for relative timestamps (every 30s)
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!current || isRunning) return;
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, [current, isRunning]);

  // Compact mode — dashboard widget
  if (compact) {
    const cardCls = isFailure
      ? 'bg-red-50 border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.15)] animate-pulse-slow'
      : 'bg-white border-gray-200';
    return (
      <div className={`border rounded-lg p-4 flex items-center gap-4 flex-wrap transition-all ${cardCls}`} data-testid="test-runner-widget">
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            {isFailure && <Warning size={18} weight="fill" className="text-red-600" />}
            🧪 Regression Tests
            {current && !isRunning && (
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${current.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800 font-bold'}`}>
                {current.success ? 'PASS' : 'FAIL'}
              </span>
            )}
          </div>
          <div className={`text-xs mt-0.5 ${isFailure ? 'text-red-800' : 'text-gray-500'}`}>
            {isRunning ? (
              <span className="text-blue-700 font-semibold">⏳ Running in background…</span>
            ) : current ? (
              <>
                <b className={current.success ? 'text-green-700' : 'text-red-700'}>
                  {current.passed || 0} pass
                </b>
                {current.failed > 0 && <> · <b className="text-red-700">{current.failed} fail</b></>}
                {current.errors > 0 && <> · <b className="text-red-700">{current.errors} err</b></>}
                {current.skipped > 0 && <> · {current.skipped} skip</>}
                {current.duration_sec != null && <> · {Number(current.duration_sec).toFixed(1)}s</>}
                <span className="text-gray-500"> · Last run: <b data-testid="test-runner-relative-time">{relativeTime(completedAt)}</b></span>
              </>
            ) : (
              <>No runs yet</>
            )}
          </div>
          {isFailure && (
            <div className="mt-2 text-xs text-red-800 font-semibold bg-red-100 border border-red-200 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="test-runner-fail-alert">
              <Warning size={12} weight="fill" />
              Production check failed — review immediately
            </div>
          )}
        </div>
        {canRun && (
          <button
            type="button"
            onClick={handleRun}
            disabled={isRunning}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-xs font-semibold ${
              isFailure
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-[#002FA7] hover:bg-[#00238a]'
            } disabled:bg-gray-400`}
            data-testid="dashboard-run-tests-btn"
          >
            {isRunning ? (
              <>
                <Clock size={14} weight="fill" className="animate-pulse" />
                Running…
              </>
            ) : (
              <>
                <Play size={14} weight="fill" />
                {isFailure ? 'Re-run' : 'Run Tests'}
              </>
            )}
          </button>
        )}
        <a
          href="/admin/test-runs"
          className="text-xs text-[#002FA7] hover:underline font-medium"
          data-testid="test-runner-widget-link"
        >
          View History →
        </a>
      </div>
    );
  }

  // Full mode — for dedicated page
  return (
    <div className="flex items-center gap-2" data-testid="test-runner-launcher">
      <button
        type="button"
        onClick={handleRun}
        disabled={!canRun || isRunning}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] hover:bg-[#00238a] disabled:bg-gray-400 text-white rounded-md text-sm font-semibold"
        data-testid="run-tests-btn"
      >
        {isRunning ? (
          <>
            <Clock size={16} weight="fill" className="animate-pulse" />
            Running…
          </>
        ) : (
          <>
            <Play size={16} weight="fill" />
            Run Tests Now
          </>
        )}
      </button>
      {current && !isRunning && (
        <span className="text-xs text-gray-600 inline-flex items-center gap-1" data-testid="launcher-inline-status">
          {current.success ? (
            <CheckCircle size={14} weight="fill" className="text-green-600" />
          ) : (
            <XCircle size={14} weight="fill" className="text-red-600" />
          )}
          {current.passed} pass · {current.failed || 0} fail
          {current.duration_sec != null && <> · {Number(current.duration_sec).toFixed(1)}s</>}
        </span>
      )}
    </div>
  );
}
